import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Skeleton,
  Alert,
  Link,
  Chip,
  TextField,
  Button,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Article,
  TrendingUp,
  Business,
  OpenInNew,
  Search,
  Assessment,
  Storage,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';

const News = () => {
  const [tabValue, setTabValue] = useState(0);
  const [portfolioNews, setPortfolioNews] = useState([]);
  const [marketNews, setMarketNews] = useState([]);
  const [mixedNews, setMixedNews] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [trendingSymbols, setTrendingSymbols] = useState([]);
  const [newsStats, setNewsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchNews();
    fetchNewsStats();
    fetchTrendingSymbols();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch portfolio, market, and mixed news
      const [portfolioResponse, marketResponse, mixedResponse] = await Promise.all([
        axios.get('/news/portfolio').catch(() => ({ data: [] })),
        axios.get('/news/market').catch(() => ({ data: [] })),
        axios.get('/news/mixed').catch(() => ({ data: [] })),
      ]);

      setPortfolioNews(portfolioResponse.data);
      setMarketNews(marketResponse.data);
      setMixedNews(mixedResponse.data);
    } catch (err) {
      setError('Failed to fetch news from database');
      console.error('News fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsStats = async () => {
    try {
      const response = await axios.get('/news/stats');
      setNewsStats(response.data);
    } catch (error) {
      console.error('Error fetching news stats:', error);
    }
  };

  const fetchTrendingSymbols = async () => {
    try {
      const response = await axios.get('/news/trending-symbols?limit=8');
      setTrendingSymbols(response.data);
    } catch (error) {
      console.error('Error fetching trending symbols:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearchLoading(true);
      const response = await axios.get(`/news/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(response.data);
      setTabValue(3); // Switch to search results tab
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search news');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const NewsCard = ({ article }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Chip
            label={article.source}
            size="small"
            variant="outlined"
            color="primary"
            icon={<Storage />}
          />
          <Typography variant="caption" color="textSecondary">
            {formatDate(article.publishedAt)}
          </Typography>
          {article.symbols && article.symbols.length > 0 && (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {article.symbols.slice(0, 3).map((symbol) => (
                <Chip
                  key={symbol}
                  label={symbol}
                  size="small"
                  variant="outlined"
                  color={symbol === 'General' ? 'secondary' : 'success'}
                />
              ))}
              {article.symbols.length > 3 && (
                <Chip
                  label={`+${article.symbols.length - 3}`}
                  size="small"
                  variant="outlined"
                  color="default"
                />
              )}
            </Box>
          )}
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ flexGrow: 1 }}>
          {article.title}
        </Typography>

        {article.description && (
          <Typography variant="body2" color="textSecondary" paragraph>
            {article.description.length > 200
              ? `${article.description.substring(0, 200)}...`
              : article.description}
          </Typography>
        )}

        <Box mt="auto">
          <Link
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Read more
            <OpenInNew fontSize="small" />
          </Link>
        </Box>
      </CardContent>
    </Card>
  );

  const EmptyState = ({ icon: Icon, title, description }) => (
    <Box textAlign="center" py={6}>
      <Icon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {description}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 2, width: '30%' }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 3, borderRadius: 2 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">
          Market News
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchNews}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* News Statistics */}
      {newsStats && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            News Database Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Total Articles</Typography>
              <Typography variant="h6">{newsStats.total_articles}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Covered Symbols</Typography>
              <Typography variant="h6">{newsStats.total_symbols}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">General Articles</Typography>
              <Typography variant="h6">{newsStats.general_articles}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Latest Update</Typography>
              <Typography variant="body2">{formatDate(newsStats.latest_article)}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            fullWidth
            label="Search news articles"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by headline or content..."
          />
          <Button
            variant="contained"
            startIcon={searchLoading ? <CircularProgress size={20} /> : <Search />}
            onClick={handleSearch}
            disabled={searchLoading || !searchQuery.trim()}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {/* Trending Symbols */}
      {trendingSymbols.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Trending Symbols (Most News Coverage)
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {trendingSymbols.map((item) => (
              <Chip
                key={item.symbol}
                label={`${item.symbol} (${item.news_count})`}
                variant="outlined"
                color="primary"
                clickable
                onClick={() => {
                  setSearchQuery(item.symbol);
                  handleSearch();
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="news tabs" variant="scrollable">
          <Tab
            icon={<TrendingUp />}
            label="Portfolio News"
            iconPosition="start"
          />
          <Tab
            icon={<Business />}
            label="Market News"
            iconPosition="start"
          />
          <Tab
            icon={<Assessment />}
            label="Mixed Feed"
            iconPosition="start"
          />
          {searchResults.length > 0 && (
            <Tab
              icon={<Search />}
              label={`Search Results (${searchResults.length})`}
              iconPosition="start"
            />
          )}
        </Tabs>
      </Box>

      {/* Portfolio News Tab */}
      {tabValue === 0 && (
        <Box>
          {portfolioNews.length === 0 ? (
            <EmptyState
              icon={Article}
              title="No Portfolio News"
              description="News related to your portfolio stocks will appear here. Add some stocks to your portfolio to see relevant news from our database."
            />
          ) : (
            <Grid container spacing={3}>
              {portfolioNews.map((article, index) => (
                <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                  <NewsCard article={article} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Market News Tab */}
      {tabValue === 1 && (
        <Box>
          {marketNews.length === 0 ? (
            <EmptyState
              icon={Business}
              title="No Market News"
              description="General market news and updates from our database will appear here."
            />
          ) : (
            <Grid container spacing={3}>
              {marketNews.map((article, index) => (
                <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                  <NewsCard article={article} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Mixed Feed Tab */}
      {tabValue === 2 && (
        <Box>
          {mixedNews.length === 0 ? (
            <EmptyState
              icon={Assessment}
              title="No Mixed News"
              description="A combination of portfolio and general market news will appear here."
            />
          ) : (
            <Grid container spacing={3}>
              {mixedNews.map((article, index) => (
                <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                  <NewsCard article={article} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Search Results Tab */}
      {tabValue === 3 && searchResults.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Search Results for "{searchQuery}" ({searchResults.length} articles)
          </Typography>
          <Grid container spacing={3}>
            {searchResults.map((article, index) => (
              <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                <NewsCard article={article} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

    </Container>
  );
};

export default News;
